/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useContext } from "react";
import { Box, Spacer, Text } from "@nordcloud/gnui";
import { RunbookInfo } from "~/components/Applications/ApplicationDetails/RunbookInfo";
import { InlineEditDatePicker } from "~/components/InlineEdit/InlineEditDatePicker";
import { InlineEditDropdown } from "~/components/InlineEdit/InlineEditDropdown";
import { InlineEditMultiSelect } from "~/components/InlineEdit/InlineEditMultiSelect";
import { InlineEditSwitch } from "~/components/InlineEdit/InlineEditSwitch";
import { InlineEditText } from "~/components/InlineEdit/InlineEditText";
import { DetailRow } from "~/components/Utils/DetailRow";
import { cloudProvidersList } from "~/constants";
import { CustomerInitialState, useCustomer } from "~/context/User/Customer";
import { useUserConsumer } from "~/context/User/User";
import { useCustomerContacts } from "~/hooks/useCustomerContacts";
import { useCustomerEmployees } from "~/hooks/useCustomerEmployees";
import { Contact, ContactBasicData } from "~/models";
import { isPrivilegedUser } from "~/routing";
import { jiraIssueKeyPattern, urlPattern } from "~/tools/validation/validators";
import { ApplicationViewCtx } from "~/views/customer/applications/ApplicationViewCtx";

function ApplicationDetailsInfo() {
  const { application, updateApplication, canEdit } = useContext(
    ApplicationViewCtx
  );

  const { role } = useUserConsumer();
  const { customer: customerInitialState, isCustomerLoading } = useCustomer();

  const [customer, setCustomer] = React.useState(CustomerInitialState);

  const contactToOption = (c: Contact | ContactBasicData) => ({
    label: c.name || c.id,
    value: c.id,
  });

  const { employees } = useCustomerEmployees();
  const customerContacts = useCustomerContacts().contacts.map(contactToOption);
  const appContacts = application.externalContacts.map(contactToOption);

  React.useEffect(() => {
    !isCustomerLoading && setCustomer(customerInitialState);
  }, [customerInitialState, isCustomerLoading]);

  const environment = process.env.REACT_APP_ENV || "";

  const ownerTooltip =
    "If a person is not available in the dropdown, make sure they are added to the NC Team in the customer view";
  const contactsTooltip =
    "If a person is not available in the dropdown, make sure they are added to the Customer Contacts in the customer view";

  return (
    <Box spacing="spacing04">
      <Text weight="medium">Application Details</Text>

      <DetailRow title="Owner" tooltip={ownerTooltip}>
        <InlineEditDropdown
          name="owner"
          options={employees}
          defaultValue={application.owner?.id || ""}
          onSubmit={(ownerId) => updateApplication({ ownerId })}
          canEdit={canEdit}
        />
      </DetailRow>

      <DetailRow title="Nordcloud team">
        <InlineEditMultiSelect
          options={employees}
          defaultValue={application.employees.map((e) => e.id)}
          onSubmit={(employeeIds) => updateApplication({ employeeIds })}
          canEdit={canEdit}
        />
      </DetailRow>

      <DetailRow title="Cloud Providers">
        <InlineEditMultiSelect
          options={cloudProvidersList}
          defaultValue={application.providers || []}
          onSubmit={(val) => updateApplication({ providers: val })}
          providerIcons
        />
      </DetailRow>

      <DetailRow title="Customer contacts" tooltip={contactsTooltip}>
        <InlineEditMultiSelect
          options={customerContacts}
          defaultValue={application.externalContacts.map((c) => c.id)}
          onSubmit={(val) => updateApplication({ externalContactIds: val })}
          canEdit={canEdit}
        />
      </DetailRow>

      <DetailRow title="Primary contacts">
        <InlineEditMultiSelect
          options={appContacts}
          defaultValue={application.externalContacts
            .filter((c) => c.primary)
            .map((c) => c.id)}
          onSubmit={(val) => updateApplication({ primaryContactIds: val })}
          canEdit={canEdit}
        />
      </DetailRow>

      <DetailRow title="Go Live Date">
        <InlineEditDatePicker
          defaultValue={application.goLiveDate || null}
          onSubmit={(goLiveDate) => updateApplication({ goLiveDate })}
          canEdit={canEdit}
        />
      </DetailRow>

      <DetailRow title="Onboarding Jira ticket">
        <InlineEditText
          name="jiraIssueKey"
          defaultValue={application.jiraIssueKey}
          onSubmit={(jiraIssueKey) => updateApplication({ jiraIssueKey })}
          canEdit={canEdit}
          validation={jiraIssueKeyPattern}
          href={`https://jira.nordcloud.com/browse/${application.jiraIssueKey}`}
        />
      </DetailRow>

      <DetailRow title="Managed by code">
        <InlineEditSwitch
          trueLabel="Yes"
          falseLabel="No"
          defaultValue={application.managedByCode}
          onSubmit={(val) => updateApplication({ managedByCode: val })}
          canEdit={canEdit}
        />
      </DetailRow>

      <DetailRow
        title="Is Nordcloud Jira integrated with customer Jira"
        minWidth={"24rem"}
      >
        <InlineEditSwitch
          trueLabel="Yes"
          falseLabel="No"
          defaultValue={application.jiraIntegrated}
          onSubmit={(val) => updateApplication({ jiraIntegrated: val })}
          canEdit={canEdit}
        />
      </DetailRow>

      <DetailRow
        title="Deployment instructions"
        tooltip={"Provide a link to GitHub or other repository"}
        minWidth={"14rem"}
      >
        <InlineEditText
          name="deploymentInstructions"
          defaultValue={application.deploymentInstructions}
          onSubmit={(val) => updateApplication({ deploymentInstructions: val })}
          canEdit={canEdit}
          validation={urlPattern}
        />
      </DetailRow>

      <Spacer height="1rem" />

      {!["production", "staging"].includes(environment) ||
      isPrivilegedUser(role) ? (
        <RunbookInfo customer={customer} />
      ) : null}
    </Box>
  );
}

export { ApplicationDetailsInfo };
